import { useState, useEffect, useRef } from 'react';
import { SpaceProps } from 'styled-system';
import { Container } from '~/components/layout';
import { OnChangeMeta } from '~/components/validating-input';
import { StyledInput } from '~/components/validating-input/validating-input.style';
import { IS_TEST } from '~/utils/constants';
import { getStyledSystemProps } from '~/utils/styled-system';

const addressMock = {
  street: '401 South 2nd Street',
  state: 'IL',
  city: 'Springfield',
  zipCode: '62701',
};

type Props = {
  label?: string;
  onChange: (address: ReturnType<typeof parseAddress> | undefined, meta: OnChangeMeta) => void;
  state?: {
    abbreviation: string;
    id: number;
    name: string;
  };
} & SpaceProps;

export const Address = ({ label = 'Address', onChange, state, ...props }: Props) => {
  const [displayValue, setDisplayValue] = useState('');
  const [isValid, setisValid] = useState(true);
  const [isValidState, setisValidState] = useState(true);
  const inputRef = useRef(null);
  let errorText = '';

  useEffect(() => {
    onChange(undefined, { isValid: false, name: 'address' });
    setisValid(true);
  }, []);

  useEffect(() => {
    if (!IS_TEST) {
      loadScript(
        `https://maps.googleapis.com/maps/api/js?key=${process.env.GOOGLE_MAPS_KEY}&libraries=places`,
        () => {
          const autoCompleteService = new window.google.maps.places.Autocomplete(
            inputRef.current!,
            {
              componentRestrictions: { country: 'us' },
              fields: ['address_components', 'formatted_address'],
              types: ['address'],
            },
          );

          autoCompleteService.addListener('place_changed', () => {
            const response = autoCompleteService.getPlace();
            const value = parseAddress(response.address_components as AddressComponent[]);
            const stateValid = state ? value.state === state.abbreviation : true;
            const valid =
              !!value.street && !!value.state && !!value.city && !!value.zipCode && stateValid;
            setisValidState(stateValid);
            setisValid(valid);
            onChange(value, { isValid: valid, name: 'address' });
            setDisplayValue(response.formatted_address || '');
          });
        },
      );
    } else {
      setisValid(true);
      onChange(addressMock, { isValid: true, name: 'address' });
    }
  }, []);

  const containerProps = getStyledSystemProps(props);

  if (!isValidState) {
    errorText = `Please enter a valid address in ${state?.name}`;
  } else if (!isValid) {
    errorText = 'Please enter a valid address';
  }

  return (
    <Container {...containerProps}>
      <StyledInput
        errorText={errorText}
        label={label}
        name="address"
        ref={inputRef}
        onChange={e => {
          if (e === '') {
            onChange(undefined, { isValid: false, name: 'address' });
          }
          setDisplayValue(e);
        }}
        value={displayValue}
        placeholder=""
      />
    </Container>
  );
};

// TODO: isTest handler
const loadScript = (url: string, callback: () => void) => {
  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.onload = () => callback();
  script.src = url;
  document.getElementsByTagName('head')[0].appendChild(script);
};

export type GoogleAddressType =
  | 'street_number'
  | 'route'
  | 'locality'
  | 'sublocality'
  | 'administrative_area_level_1'
  | 'administrative_area_level_3'
  | 'postal_code';

/* eslint-disable camelcase */

type AddressComponent = {
  long_name: string;
  short_name: string;
  types: GoogleAddressType[];
};

export const parseAddress = (addressComponents: AddressComponent[]) => {
  const find = (field: GoogleAddressType, variant: 'long_name' | 'short_name' = 'long_name') =>
    addressComponents.find(c => c.types.find(t => t === field))?.[variant];

  const streetNumber = find('street_number');
  const streetName = find('route');
  return {
    street: streetNumber && streetName ? `${streetNumber} ${streetName}` : undefined,
    state: find('administrative_area_level_1', 'short_name'),
    city: find('locality') || find('sublocality') || find('administrative_area_level_3'),
    zipCode: find('postal_code'),
  };
};

/* eslint-disable camelcase */
